import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Clock, ExternalLink, RefreshCw, PlusCircle, Search
} from 'lucide-react'
import { assessmentsApi } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { verdictBadgeColor, verdictLabel, statusLabel, formatDate, appCategoryLabel } from '@/lib/utils'
import type { AssessmentListItem } from '@/types'

export default function AssessmentsHistory() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Debounce: interroga il server solo quando l'utente smette di digitare
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: assessments, isLoading } = useQuery({
    queryKey: ['assessments', 'history', page, search],
    queryFn: () => assessmentsApi.list({ page, page_size: 20, search: search || undefined }),
  })

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Clock className="w-6 h-6 text-mim-blue" />
          {t('history.title')}
        </h1>
        <Badge variant="outline">{assessments?.length || 0} {t('history.items')}</Badge>
      </div>

      {/* Ricerca per nome dello strumento */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('history.searchPlaceholder')}
          className="pl-9"
          aria-label={t('history.searchAria')}
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : assessments && assessments.length > 0 ? (
        <div className="space-y-3">
          {assessments.map((item: AssessmentListItem) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-lg mb-1">{item.app.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {appCategoryLabel(item.app.category, i18n.language)}
                      </Badge>
                      {item.status === 'COMPLETED' && item.verdict ? (
                        <Badge className={verdictBadgeColor(item.verdict)}>
                          {verdictLabel(item.verdict, i18n.language)}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          {statusLabel(item.status, i18n.language)}
                        </Badge>
                      )}
                      {item.review_status === 'PENDING' && (
                        <Badge variant="outline" className="text-xs border-giallo text-giallo-dark">
                          {t('assessment.review.pendingBadge')}
                        </Badge>
                      )}
                      {item.overall_score && (
                        <span className="text-xs text-muted-foreground">
                          Score: {item.overall_score.toFixed(1)}/5
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(item.created_at, i18n.language)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/assessment/${item.id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('common.open')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : search ? (
        <div className="text-center py-16 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>{t('history.noResults', { search })}</p>
        </div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>{t('history.empty')}</p>
          <Button
            variant="link"
            className="mt-2 text-mim-blue"
            onClick={() => navigate('/assessment/new')}
          >
            {t('history.createFirst')}
          </Button>
        </div>
      )}
    </div>
  )
}
